import React, { useMemo, useRef, useState } from 'react';
import { CheckCircle2, Pencil, Plus, Power, PowerOff, Printer, QrCode } from 'lucide-react';
import { Button } from '../../Button';
import { describeError } from '../../../services/errorMessage';
import {
    alleUmschalten,
    auswahlBereinigen,
    auswahlUmschalten,
    kartenEingaben,
    offeneJeArtikel,
    sortiereArtikel,
    statusChip,
    type ArtikelWerte,
} from '../../../services/material/materialLogic';
import type { MaterialArtikel, MaterialMeldung } from '../../../services/material/materialTypes';
import { ArtikelDialog } from './ArtikelDialog';
import { KarteErneuernDialog } from './KarteErneuernDialog';
import { KartenDruckDialog } from './KartenDruckDialog';
import { AktivChip, StatusChipAnzeige } from './MaterialChips';

/**
 * Reiter „Artikel“ (nur mit `material.verwalten`): Liste mit Status-Chip und
 * aktiv/deaktiviert, Anlegen/Bearbeiten, Deaktivieren/Aktivieren, Karte
 * erneuern (mit Rückfrage) und Mehrfachauswahl → „Karten drucken“.
 * Deaktivierte Artikel lassen sich nicht drucken — ihre Karte gilt nicht.
 */

interface Props {
    artikel: MaterialArtikel[];
    meldungen: MaterialMeldung[];
    nurLesen: boolean;
    onSpeichern: (id: string | null, werte: ArtikelWerte, aktiv: boolean) => Promise<unknown>;
    onTokenNeu: (artikelId: string) => Promise<unknown>;
}

type Dialog =
    | { art: 'neu' }
    | { art: 'bearbeiten'; artikel: MaterialArtikel }
    | { art: 'erneuern'; artikel: MaterialArtikel }
    | { art: 'drucken' };

export const ArtikelReiter: React.FC<Props> = ({ artikel, meldungen, nurLesen, onSpeichern, onTokenNeu }) => {
    const liste = useMemo(() => sortiereArtikel(artikel), [artikel]);
    const offene = useMemo(() => offeneJeArtikel(meldungen), [meldungen]);

    const [roheAuswahl, setAuswahl] = useState<ReadonlySet<string>>(new Set());
    // Nach dem Neuladen zählt nur, was noch da und aktiv ist.
    const auswahl = useMemo(() => auswahlBereinigen(roheAuswahl, liste), [roheAuswahl, liste]);
    const aktive = liste.filter(a => a.aktiv);
    const alleGewaehlt = aktive.length > 0 && aktive.every(a => auswahl.has(a.id));

    const [dialog, setDialog] = useState<Dialog | null>(null);
    const [laeuft, setLaeuft] = useState<string | null>(null);
    const laeuftRef = useRef(false);
    const [zeilenFehler, setZeilenFehler] = useState<{ id: string; text: string } | null>(null);
    const [hinweis, setHinweis] = useState('');

    const aktivSchalten = async (a: MaterialArtikel) => {
        if (laeuftRef.current) return;
        laeuftRef.current = true;
        setLaeuft(a.id);
        setZeilenFehler(null);
        setHinweis('');
        try {
            await onSpeichern(a.id, { name: a.name, lagerort: a.lagerort, hinweis: a.hinweis }, !a.aktiv);
        } catch (e) {
            setZeilenFehler({ id: a.id, text: describeError(e) });
        } finally {
            laeuftRef.current = false;
            setLaeuft(null);
        }
    };

    const erneuert = (a: MaterialArtikel) => {
        setDialog(null);
        // Die neue Karte gleich zum Drucken vormerken.
        setAuswahl(alt => new Set(alt).add(a.id));
        setHinweis(`Neue Karte für „${a.name}“ — bitte drucken und die alte ersetzen. Die alte Karte gilt nicht mehr.`);
    };

    const karten = dialog?.art === 'drucken' ? kartenEingaben(liste, auswahl) : [];

    return (
        <div className="space-y-4">
            {!nurLesen && (
                <div className="flex flex-wrap items-center gap-2">
                    <Button icon={<Plus className="w-4 h-4" />} className="min-h-[44px]" onClick={() => { setHinweis(''); setDialog({ art: 'neu' }); }}>
                        Neuer Artikel
                    </Button>
                    <Button
                        variant="secondary"
                        className="min-h-[44px]"
                        icon={<Printer className="w-4 h-4" />}
                        disabled={auswahl.size === 0}
                        onClick={() => setDialog({ art: 'drucken' })}
                        title={auswahl.size === 0 ? 'Erst Artikel auswählen' : undefined}
                    >
                        Karten drucken{auswahl.size > 0 ? ` (${auswahl.size})` : ''}
                    </Button>
                    {aktive.length > 0 && (
                        <label className="ml-auto flex items-center gap-2 text-xs font-semibold text-slate-600 min-h-[44px] px-2 cursor-pointer">
                            <input
                                type="checkbox"
                                className="w-4 h-4"
                                checked={alleGewaehlt}
                                onChange={() => setAuswahl(alleUmschalten(auswahl, liste))}
                            />
                            Alle aktiven auswählen
                        </label>
                    )}
                </div>
            )}

            {hinweis && (
                <p role="status" className="flex items-start gap-2 text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-xl p-3">
                    <CheckCircle2 className="w-4 h-4 shrink-0" /> {hinweis}
                </p>
            )}

            {liste.length === 0 ? (
                <p className="text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-xl p-3">
                    Noch keine Artikel. {nurLesen ? '' : 'Mit „Neuer Artikel“ anlegen, dann die Karte drucken.'}
                </p>
            ) : (
                <ul className="space-y-2">
                    {liste.map(a => {
                        const gewaehlt = auswahl.has(a.id);
                        return (
                            <li
                                key={a.id}
                                data-glass="karte"
                                className={`bg-white border rounded-2xl p-3 sm:p-4 ${gewaehlt ? 'border-blue-300 ring-1 ring-blue-200' : 'border-slate-200'} ${a.aktiv ? '' : 'opacity-75'}`}
                            >
                                <div className="flex items-start gap-3">
                                    {!nurLesen && (
                                        <input
                                            type="checkbox"
                                            className="w-5 h-5 mt-0.5 shrink-0"
                                            checked={gewaehlt}
                                            disabled={!a.aktiv}
                                            onChange={() => setAuswahl(auswahlUmschalten(auswahl, a.id))}
                                            aria-label={`${a.name} zum Drucken auswählen`}
                                            title={a.aktiv ? undefined : 'Deaktivierte Artikel haben keine gültige Karte.'}
                                        />
                                    )}
                                    <div className="min-w-0 flex-1">
                                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                                            <span className="font-bold text-slate-800 break-words">{a.name}</span>
                                            <StatusChipAnzeige chip={statusChip(offene.get(a.id)?.status)} />
                                            <AktivChip aktiv={a.aktiv} />
                                        </div>
                                        {a.lagerort && <p className="text-xs text-slate-500 break-words">{a.lagerort}</p>}
                                        {a.hinweis && <p className="text-xs text-slate-400 break-words line-clamp-2">{a.hinweis}</p>}
                                        {!nurLesen && (
                                            <div className="mt-2 flex flex-wrap gap-1">
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="min-h-[40px]"
                                                    icon={<Pencil className="w-3.5 h-3.5" />}
                                                    disabled={laeuft !== null}
                                                    onClick={() => { setHinweis(''); setDialog({ art: 'bearbeiten', artikel: a }); }}
                                                >
                                                    Bearbeiten
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="min-h-[40px]"
                                                    icon={a.aktiv ? <PowerOff className="w-3.5 h-3.5" /> : <Power className="w-3.5 h-3.5" />}
                                                    loading={laeuft === a.id}
                                                    disabled={laeuft !== null}
                                                    onClick={() => { void aktivSchalten(a); }}
                                                    title={a.aktiv ? 'Karte wird ungültig; der Verlauf bleibt.' : 'Die bisherige Karte gilt wieder.'}
                                                >
                                                    {a.aktiv ? 'Deaktivieren' : 'Aktivieren'}
                                                </Button>
                                                {a.aktiv && (
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        className="min-h-[40px]"
                                                        icon={<QrCode className="w-3.5 h-3.5" />}
                                                        disabled={laeuft !== null}
                                                        onClick={() => { setHinweis(''); setDialog({ art: 'erneuern', artikel: a }); }}
                                                    >
                                                        Karte erneuern
                                                    </Button>
                                                )}
                                            </div>
                                        )}
                                        {zeilenFehler?.id === a.id && <p role="alert" className="mt-1 text-xs text-red-700">{zeilenFehler.text}</p>}
                                    </div>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}

            {dialog?.art === 'neu' && (
                <ArtikelDialog artikel={null} onSpeichern={werte => onSpeichern(null, werte, true)} onSchliessen={() => setDialog(null)} />
            )}
            {dialog?.art === 'bearbeiten' && (
                <ArtikelDialog
                    artikel={dialog.artikel}
                    onSpeichern={werte => onSpeichern(dialog.artikel.id, werte, dialog.artikel.aktiv)}
                    onSchliessen={() => setDialog(null)}
                />
            )}
            {dialog?.art === 'erneuern' && (
                <KarteErneuernDialog
                    artikel={dialog.artikel}
                    onErneuern={async () => { await onTokenNeu(dialog.artikel.id); erneuert(dialog.artikel); }}
                    onAbbrechen={() => setDialog(null)}
                />
            )}
            {dialog?.art === 'drucken' && <KartenDruckDialog karten={karten} onSchliessen={() => setDialog(null)} />}
        </div>
    );
};
